import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ShoppingCart, X, ArrowRight, Check } from 'lucide-react';

import { getStorefrontConfig, isSinglePageTemplateConfig } from '../../utils/storefrontConfig';

interface AddToCartModalProps {
  isOpen: boolean;
  onClose: () => void;
  productName: string;
  productImage?: string;
  quantity: number;
}



export const AddToCartModal = ({
  isOpen,
  onClose,
  productName,
  productImage,
  quantity
}: AddToCartModalProps) => {
  console.log('[AddToCartModal] Component START, isOpen:', isOpen);
  
  const [showCartButton, setShowCartButton] = useState(true);

  // Detect funnel slug from URL (e.g., /f/challenge -> 'challenge')
  // Use window.location instead of useLocation() since CartProvider is outside Router
  const funnelSlug = (() => {
    try {
      const pathname = window.location.pathname;
      console.log('[AddToCartModal] Current pathname:', pathname);
      const funnelMatch = pathname.match(/^\/f\/([^\/]+)/);
      const slug = funnelMatch ? funnelMatch[1] : undefined;
      console.log('[AddToCartModal] Detected funnelSlug:', slug);
      return slug;
    } catch (error) {
      console.error('[AddToCartModal] ERROR detecting funnelSlug:', error);
      return undefined;
    }
  })();

  useEffect(() => {
    if (isOpen) {
      const checkConfig = async () => {
        try {
          console.log('[AddToCartModal] useEffect checkConfig START, funnelSlug:', funnelSlug);
          const config = await getStorefrontConfig(undefined, funnelSlug);
          console.log('[AddToCartModal] Config loaded:', !!config, 'template:', config?.template?.slug);
          const availablePages = config?.template?.availablePages || config?.availablePages || [];

          // Single page check (matching logic in handlers)
          const isSinglePageTemplate = isSinglePageTemplateConfig(config);

          // Show "View cart" if:
          // 1. It IS a single page template (button triggers sidebar)
          // 2. OR availablePages is empty (Legacy/Default behavior)
          // 3. OR 'cart' is explicitly listed in availablePages
          const shouldShow = isSinglePageTemplate || availablePages.length === 0 || availablePages.includes('cart');

          console.log('[AddToCartModal] Should show cart button:', shouldShow);
          setShowCartButton(shouldShow);
        } catch (error) {
          console.error('[AddToCartModal] Error checking storefront config:', error);
          setShowCartButton(true); // Fallback to safe default
        }
      };
      checkConfig();
    }
  }, [isOpen, funnelSlug]);


  const handleCheckout = async () => {
    console.log('[AddToCartModal] handleCheckout called, funnelSlug:', funnelSlug);
    onClose();

    // Check if we're on a single-page template
    try {
      const config = await getStorefrontConfig(undefined, funnelSlug);
      const isSinglePageTemplate = isSinglePageTemplateConfig(config);

      if (isSinglePageTemplate) {
        const checkoutStyle = config?.checkoutStyle || 'sidebar';

        if (checkoutStyle === 'sidebar') {
          // Dispatch event to open checkout sidebar
          window.dispatchEvent(new CustomEvent('openCheckoutSidebar'));
        } else {
          // Scroll to checkout section
          setTimeout(() => {
            const checkoutSection = document.getElementById('checkout-section') ||
              document.querySelector('[data-section-id="checkout-section"]');
            if (checkoutSection) {
              checkoutSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
          }, 300);
        }
        return;
      }
    } catch (error) {
      console.error('Failed to check storefront config:', error);
    }

    // Default behavior: redirect to checkout page (preserve funnel context if on funnel page)
    const checkoutPath = funnelSlug ? `/f/${funnelSlug}/checkout` : '/checkout';
    setTimeout(() => {
      window.location.href = checkoutPath;
    }, 300);
  };

  const handleViewCart = async () => {
    console.log('[AddToCartModal] handleViewCart called');
    onClose();

    // Check if we're on a single-page template
    try {
      const config = await getStorefrontConfig(undefined, funnelSlug);
      const isSinglePageTemplate = isSinglePageTemplateConfig(config);

      if (isSinglePageTemplate) {
        const checkoutStyle = config?.checkoutStyle || 'sidebar';

        if (checkoutStyle === 'sidebar') {
          // Dispatch event to open cart sidebar
          window.dispatchEvent(new CustomEvent('openCartSidebar'));
        } else {
          // Scroll to checkout section
          setTimeout(() => {
            const checkoutSection = document.getElementById('checkout-section') ||
              document.querySelector('[data-section-id="checkout-section"]');
            if (checkoutSection) {
              checkoutSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
          }, 300);
        }
        return;
      }
    } catch (error) {
      console.error('Failed to check storefront config:', error);
    }

    // Default behavior: redirect to cart page
    setTimeout(() => {
      window.location.href = '/cart';
    }, 300);
  };

  console.log('[AddToCartModal] Rendering, isOpen:', isOpen, 'product:', productName);

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            onClick={() => {
              console.log('[AddToCartModal] Backdrop clicked');
              onClose();
            }}
            className="fixed inset-0 bg-black/50 z-[120]"
          />

          {/* Modal - Slides up from bottom */}
          <div className="fixed inset-0 flex items-end justify-center z-[120] p-4 pointer-events-none sm:items-center sm:p-6">
            <motion.div
              initial={{ opacity: 0, y: '100%' }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: '100%' }}
              transition={{
                type: 'spring',
                damping: 30,
                stiffness: 300,
                duration: 0.4
              }}
              className="
                rounded-t-[32px] sm:rounded-[28px]
                border border-gray-200
                shadow-xl
                max-w-md w-full
                pointer-events-auto
                overflow-hidden
                bg-white
              "
              onClick={(e) => e.stopPropagation()}
            >
              {/* Success Banner */}
              <div
                className="px-6 py-5 flex items-center justify-center gap-3 relative"
                style={{
                  background: 'var(--primary-color, #007AFF)',
                }}
              >
                <div className="w-8 h-8 rounded-full bg-white/25 flex items-center justify-center flex-shrink-0">
                  <Check className="w-5 h-5 text-white" strokeWidth={3} />
                </div>
                <h2 className="text-xl font-bold text-white tracking-tight">
                  Added to your cart!
                </h2>
                {/* Close Button */}
                <button
                  onClick={() => {
                    console.log('[AddToCartModal] Close button clicked');
                    onClose();
                  }}
                  className="absolute top-4 right-4 w-8 h-8 rounded-full bg-white/25 hover:bg-white/35 transition-colors duration-200 flex items-center justify-center focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-white/50"
                  aria-label="Close modal"
                >
                  <X className="w-5 h-5 text-white" />
                </button>
              </div>

              {/* Content */}
              <div className="p-6">
                {/* Product Info */}
                <div className="flex items-center gap-4 mb-6">
                  {productImage ? (
                    <div className="w-20 h-20 rounded-[20px] overflow-hidden bg-gray-100 flex-shrink-0 border border-gray-200">
                      <img
                        src={productImage}
                        alt={productName}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  ) : (
                    <div className="w-20 h-20 rounded-[20px] bg-gray-100 flex items-center justify-center flex-shrink-0 border border-gray-200">
                      <ShoppingCart className="w-8 h-8 text-neutral-400" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="font-bold text-neutral-900 text-lg mb-1 truncate">{productName}</p>
                    <p className="text-sm text-neutral-600 font-medium">Quantity: <span className="tabular-nums">{quantity}</span></p>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="space-y-3">
                  <button
                    onClick={handleCheckout}
                    className="
                      w-full
                      py-4 px-6
                      rounded-[20px]
                      font-bold text-base
                      text-white
                      transition-all duration-200
                      flex items-center justify-center gap-2
                      group
                      hover:scale-[0.98]
                      active:scale-[0.96]
                      focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[var(--primary-color)]
                      shadow-lg hover:shadow-xl
                    "
                    style={{ backgroundColor: 'var(--primary-color, #007AFF)' }}
                  >
                    CHECKOUT
                    <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                  </button>
                  {showCartButton && (
                    <button
                      onClick={handleViewCart}
                      className="
                        w-full
                        py-4 px-6
                        rounded-[20px]
                        font-semibold text-base
                        text-neutral-700
                        bg-gray-100
                        hover:bg-gray-200
                        border border-gray-200
                        transition-colors duration-200
                        focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-neutral-400
                      "
                    >
                      View cart
                    </button>
                  )}
                </div>
              </div>
            </motion.div>
          </div>

        </>
      )}
    </AnimatePresence>
  );
};
